import { useListState } from "@/hooks/use-list-state";
import { useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { DEFAULT_ACCENT_COLOR } from "@/lib/brand-colors";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ExternalLink, Globe, Users, Settings, Eye, Mail, Phone, Calendar, Plus, Trash2, Star, Palette, Type, MessageSquare, FileText, Upload, Image, Home, Link2, Copy, Pencil, Check, X, AlertTriangle, Info } from "lucide-react";
import type { MarketingSettings, DirectInquiryLead, ReferralLink, Property, LandingPage, PropertyGroup } from "@shared/schema";
import { format } from "date-fns";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";

type LandingPageWithCount = LandingPage & { activePropertyCount?: number; totalPropertyCount?: number };

type LandingPageLeadCounts = { primary: number; bySlug: Record<string, number> };

function cleanSlug(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}

export default function AppMarketingDirectBookings() {
  const queryClient = useQueryClient();
  usePageTitle("Direct Bookings");
  const { toast } = useToast();
  const [editingSettings, setEditingSettings] = useState(false);
  const [formData, setFormData] = useState<{
    headline: string;
    description: string;
    tagline: string;
    ctaButtonText: string;
    contactPhone: string;
    contactEmail: string;
    accentColor: string;
    footerText: string;
    brandName: string;
    brandLogoUrl: string;
  } | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [showAddLink, setShowAddLink] = useState(false);
  const [newLinkMarket, setNewLinkMarket] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");

  const [editingLinkIndex, setEditingLinkIndex] = useState<number | null>(null);
  const [editLinkMarket, setEditLinkMarket] = useState("");
  const [editLinkUrl, setEditLinkUrl] = useState("");

  const [showAddPage, setShowAddPage] = useState(false);
  const [newPageSlug, setNewPageSlug] = useState("");
  const [newPageGroupId, setNewPageGroupId] = useState<string>("");
  const [newPageSlugError, setNewPageSlugError] = useState("");

  const [leadSourceFilter, setLeadSourceFilter] = useListState<string>("direct-bookings:source", "all");

  const [drawerPage, setDrawerPage] = useState<LandingPageWithCount | null>(null);
  const [drawerFormData, setDrawerFormData] = useState<{
    slug: string;
    propertyGroupId: string;
    headline: string;
    description: string;
    tagline: string;
    ctaButtonText: string;
    contactPhone: string;
    contactEmail: string;
    accentColor: string;
    brandName: string;
    brandLogoUrl: string;
    footerText: string;
  } | null>(null);
  const [drawerSlugError, setDrawerSlugError] = useState("");
  const [drawerUploadingLogo, setDrawerUploadingLogo] = useState(false);

  const [showPrimaryGearSheet, setShowPrimaryGearSheet] = useState(false);
  const [showPrimaryHouseSheet, setShowPrimaryHouseSheet] = useState(false);
  const [pageHouseSheetPage, setPageHouseSheetPage] = useState<LandingPageWithCount | null>(null);
  const [pageHouseSheetEnabledIds, setPageHouseSheetEnabledIds] = useState<number[]>([]);

  const settingsQuery = useQuery<MarketingSettings | null>({
    queryKey: ["/api/app/marketing/settings"],
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const leadsQuery = useQuery<DirectInquiryLead[]>({
    queryKey: ["/api/app/marketing/direct-inquiry-leads"],
  });

  const landingPagesQuery = useQuery<LandingPageWithCount[]>({
    queryKey: ["/api/app/marketing/landing-pages"],
  });

  const propertyGroupsQuery = useQuery<PropertyGroup[]>({
    queryKey: ["/api/app/property-groups"],
  });

  const leadCountsQuery = useQuery<LandingPageLeadCounts>({
    queryKey: ["/api/app/marketing/landing-page-lead-counts"],
  });

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      return apiRequest("POST", "/api/app/marketing/settings", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/marketing/settings"] });
      setEditingSettings(false);
      setFormData(null);
      toast({ title: "Settings saved" });
    },
    onError: () => {
      toast({ title: "Failed to save settings", variant: "destructive" });
    },
  });

  const createPageMutation = useMutation({
    mutationFn: async (data: { slug: string; propertyGroupId: number }) => {
      const res = await apiRequest("POST", "/api/app/marketing/landing-pages", data);
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || "Failed to create landing page");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/marketing/landing-pages"] });
      setShowAddPage(false);
      setNewPageSlug("");
      setNewPageGroupId("");
      setNewPageSlugError("");
      toast({ title: "Landing page created" });
    },
    onError: (err: Error) => {
      if (String(err.message ?? "").toLowerCase().includes("slug")) {
        setNewPageSlugError(parseErrorMessage(err, "That URL is unavailable. Please try another."));
      } else {
        toast({ title: err.message, variant: "destructive" });
      }
    },
  });

  const updatePageMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Record<string, unknown> }) => {
      const res = await apiRequest("PATCH", `/api/app/marketing/landing-pages/${id}`, data);
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || "Failed to update landing page");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/marketing/landing-pages"] });
      setDrawerPage(null);
      setDrawerFormData(null);
      setDrawerSlugError("");
      toast({ title: "Landing page updated" });
    },
    onError: (err: Error) => {
      if (String(err.message ?? "").toLowerCase().includes("slug")) {
        setDrawerSlugError(parseErrorMessage(err, "That URL is unavailable. Please try another."));
      } else {
        toast({ title: err.message, variant: "destructive" });
      }
    },
  });

  const deletePageMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/marketing/landing-pages/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/marketing/landing-pages"] });
      toast({ title: "Landing page deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete landing page", variant: "destructive" });
    },
  });

  const updatePageVisibilityMutation = useMutation({
    mutationFn: async ({ id, enabledPropertyIds }: { id: number; enabledPropertyIds: number[] | null }) => {
      const res = await apiRequest("PATCH", `/api/app/marketing/landing-pages/${id}`, { enabledPropertyIds });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || "Failed to update visibility");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/marketing/landing-pages"] });
      setPageHouseSheetPage(null);
      toast({ title: "Visibility updated" });
    },
    onError: () => {
      toast({ title: "Failed to update visibility", variant: "destructive" });
    },
  });

  const settings = settingsQuery.data;
  const referralLinks: ReferralLink[] = Array.isArray(settings?.referralLinks) ? (settings.referralLinks as ReferralLink[]) : [];
  const secondaryPages: LandingPageWithCount[] = landingPagesQuery.data ?? [];
  const propertyGroups: PropertyGroup[] = propertyGroupsQuery.data ?? [];

  const userId: number | undefined = settings?.userId ?? undefined;
  const primaryUrl = userId ? `/rooms-for-rent/u-${userId}` : "/rooms-for-rent";
  const primaryLeadCount = leadCountsQuery.data?.primary ?? 0;

  function startEditing() {
    setFormData({
      headline: settings?.headline || "Rooms for Rent",
      description: settings?.description || "",
      tagline: settings?.tagline || "",
      ctaButtonText: settings?.ctaButtonText || "Start Free Application",
      contactPhone: settings?.contactPhone || "",
      contactEmail: settings?.contactEmail || "",
      accentColor: settings?.accentColor || DEFAULT_ACCENT_COLOR,
      footerText: settings?.footerText || "",
      brandName: settings?.brandName || "",
      brandLogoUrl: settings?.brandLogoUrl || "",
    });
    setEditingSettings(true);
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !formData) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast({ title: "Only image files (PNG, JPG, WebP, GIF) are allowed", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Logo must be under 5MB", variant: "destructive" });
      return;
    }
    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await apiRequest("POST", "/api/app/upload", {
        filename: file.name,
        mimeType: file.type,
        data: base64,
      });
      const json = await res.json();
      setFormData({ ...formData, brandLogoUrl: json.imageUrl });
      toast({ title: "Logo uploaded" });
    } catch {
      toast({ title: "Failed to upload logo", variant: "destructive" });
    } finally {
      setUploadingLogo(false);
    }
  }

  function cancelEditing() {
    setEditingSettings(false);
    setFormData(null);
  }

  function handleAddLink() {
    if (!newLinkMarket.trim() || !newLinkUrl.trim()) return;
    try {
      new URL(newLinkUrl);
    } catch {
      toast({ title: "Please enter a valid URL", variant: "destructive" });
      return;
    }
    const isFirst = referralLinks.length === 0;
    const updated = [...referralLinks, { marketName: newLinkMarket.trim(), url: newLinkUrl.trim(), isDefault: isFirst }];
    saveMutation.mutate({ referralLinks: updated });
    setNewLinkMarket("");
    setNewLinkUrl("");
    setShowAddLink(false);
  }

  function handleRemoveLink(index: number) {
    const updated = referralLinks.filter((_, i) => i !== index);
    if (updated.length > 0 && !updated.some(l => l.isDefault)) {
      updated[0].isDefault = true;
    }
    saveMutation.mutate({ referralLinks: updated });
  }

  function handleSetDefault(index: number) {
    const updated = referralLinks.map((l, i) => ({ ...l, isDefault: i === index }));
    saveMutation.mutate({ referralLinks: updated });
  }

  function startEditLink(index: number) {
    setEditingLinkIndex(index);
    setEditLinkMarket(referralLinks[index].marketName);
    setEditLinkUrl(referralLinks[index].url);
  }

  function cancelEditLink() {
    setEditingLinkIndex(null);
    setEditLinkMarket("");
    setEditLinkUrl("");
  }

  function handleSaveEditLink() {
    if (editingLinkIndex === null) return;
    if (!editLinkMarket.trim() || !editLinkUrl.trim()) return;
    try {
      new URL(editLinkUrl);
    } catch {
      toast({ title: "Please enter a valid URL", variant: "destructive" });
      return;
    }
    const updated = referralLinks.map((l, i) =>
      i === editingLinkIndex ? { ...l, marketName: editLinkMarket.trim(), url: editLinkUrl.trim() } : l
    );
    saveMutation.mutate({ referralLinks: updated });
    cancelEditLink();
  }

  function handleAddPage() {
    setNewPageSlugError("");
    const slug = cleanSlug(newPageSlug);
    if (!slug) {
      setNewPageSlugError("Please enter a valid slug.");
      return;
    }
    const groupId = parseInt(newPageGroupId, 10);
    if (!groupId || isNaN(groupId)) {
      setNewPageSlugError("Please select a property group.");
      return;
    }
    createPageMutation.mutate({ slug, propertyGroupId: groupId });
  }

  function openDrawer(page: LandingPageWithCount) {
    setShowPrimaryGearSheet(false);
    setShowPrimaryHouseSheet(false);
    setPageHouseSheetPage(null);
    cancelEditing();
    setDrawerPage(page);
    setDrawerFormData({
      slug: page.slug,
      propertyGroupId: String(page.propertyGroupId),
      headline: page.headline || "",
      description: page.description || "",
      tagline: page.tagline || "",
      ctaButtonText: page.ctaButtonText || "",
      contactPhone: page.contactPhone || "",
      contactEmail: page.contactEmail || "",
      accentColor: page.accentColor || "",
      brandName: page.brandName || "",
      brandLogoUrl: page.brandLogoUrl || "",
      footerText: page.footerText || "",
    });
    setDrawerSlugError("");
  }

  function closeDrawer() {
    setDrawerPage(null);
    setDrawerFormData(null);
    setDrawerSlugError("");
  }

  function handleSaveDrawer() {
    if (!drawerPage || !drawerFormData) return;
    setDrawerSlugError("");
    const groupId = parseInt(drawerFormData.propertyGroupId, 10);
    if (!groupId || isNaN(groupId)) {
      setDrawerSlugError("Please select a property group.");
      return;
    }
    const toNull = (v: string) => v.trim() === "" ? null : v.trim();
    const toPublicLogoUrl = (url: string | null | undefined) => {
      if (!url) return url ?? null;
      return url.replace("/api/app/images/", "/api/public/images/");
    };
    updatePageMutation.mutate({
      id: drawerPage.id,
      data: {
        propertyGroupId: groupId,
        headline: toNull(drawerFormData.headline),
        description: toNull(drawerFormData.description),
        tagline: toNull(drawerFormData.tagline),
        ctaButtonText: toNull(drawerFormData.ctaButtonText),
        contactPhone: toNull(drawerFormData.contactPhone),
        contactEmail: toNull(drawerFormData.contactEmail),
        accentColor: toNull(drawerFormData.accentColor),
        brandName: toNull(drawerFormData.brandName),
        brandLogoUrl: toPublicLogoUrl(toNull(drawerFormData.brandLogoUrl)),
        footerText: toNull(drawerFormData.footerText),
      },
    });
  }

  async function handleDrawerLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !drawerFormData) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast({ title: "Only image files (PNG, JPG, WebP, GIF) are allowed", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Logo must be under 5MB", variant: "destructive" });
      return;
    }
    setDrawerUploadingLogo(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await apiRequest("POST", "/api/app/upload", {
        filename: file.name,
        mimeType: file.type,
        data: base64,
      });
      const json = await res.json();
      setDrawerFormData({ ...drawerFormData, brandLogoUrl: json.imageUrl });
      toast({ title: "Logo uploaded" });
    } catch {
      toast({ title: "Failed to upload logo", variant: "destructive" });
    } finally {
      setDrawerUploadingLogo(false);
    }
  }

  const allProperties = propertiesQuery.data ?? [];
  const enabledPropertyIds: number[] = Array.isArray(settings?.enabledPropertyIds) ? (settings.enabledPropertyIds as number[]) : [];
  const leads = leadsQuery.data ?? [];
  const filteredLeads = leads.filter((lead) => {
    if (leadSourceFilter === "all") return true;
    if (leadSourceFilter === "primary") return !lead.landingPageSlug;
    if (leadSourceFilter.startsWith("slug:")) {
      return lead.landingPageSlug === leadSourceFilter.slice(5);
    }
    return true;
  });

  function toggleProperty(propertyId: number, enabled: boolean) {
    let updated: number[];
    if (enabled) {
      updated = [...enabledPropertyIds, propertyId];
    } else {
      updated = enabledPropertyIds.filter(id => id !== propertyId);
    }
    saveMutation.mutate({ enabledPropertyIds: updated });
  }

  function closeAllSheets() {
    setShowPrimaryGearSheet(false);
    setShowPrimaryHouseSheet(false);
    setPageHouseSheetPage(null);
    setDrawerPage(null);
    setDrawerFormData(null);
    setDrawerSlugError("");
    cancelEditing();
  }

  function openPageHouseSheet(page: LandingPageWithCount) {
    closeAllSheets();
    const ids = Array.isArray(page.enabledPropertyIds) ? (page.enabledPropertyIds as number[]) : [];
    setPageHouseSheetEnabledIds(ids);
    setPageHouseSheetPage(page);
  }

  function openPrimaryGearSheet() {
    closeAllSheets();
    startEditing();
    setShowPrimaryGearSheet(true);
  }

  function closePrimaryGearSheet() {
    cancelEditing();
    setShowPrimaryGearSheet(false);
  }

  function handleSavePrimaryGear() {
    if (!formData) return;
    const dataToSave = {
      ...formData,
      brandLogoUrl: formData.brandLogoUrl
        ? formData.brandLogoUrl.replace("/api/app/images/", "/api/public/images/")
        : formData.brandLogoUrl,
    };
    saveMutation.mutate(dataToSave, {
      onSuccess: () => {
        setShowPrimaryGearSheet(false);
      },
    });
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(window.location.origin + text).then(() => {
      toast({ title: "URL copied to clipboard" });
    }).catch(() => {
      toast({ title: "Failed to copy", variant: "destructive" });
    });
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-tour="direct-bookings-heading">Direct Bookings</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Drive prospective members to apply via your PadSplit referral link and bypass the 10-day booking fee.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => window.open(primaryUrl, "_blank")}
              data-testid="button-preview-landing-page"
              data-tour="direct-bookings-link"
            >
              <Eye className="h-4 w-4 mr-1.5" />
              Preview Landing Page
            </Button>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="direct-bookings" />
              <GuideButton pageKey="direct-bookings" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="direct-bookings" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card data-testid="card-total-leads">
            <CardContent className="pt-5">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{leads.length}</p>
                  <p className="text-xs text-gray-500">Total Inquiries</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card
            data-testid="card-landing-page"
            className="cursor-pointer hover-elevate active-elevate-2"
            onClick={() => {
              document.querySelector('[data-testid="card-landing-page-url"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            <CardContent className="pt-5">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-green-50 flex items-center justify-center">
                  <Globe className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Landing Pages</p>
                  <p className="text-xs text-gray-500" data-testid="text-landing-page-count">
                    1 primary + {secondaryPages.length} secondary
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="card-referral-links-summary">
            <CardContent className="pt-5">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-purple-50 flex items-center justify-center">
                  <ExternalLink className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Referral Links</p>
                  <p className="text-xs text-gray-500">
                    {referralLinks.length > 0 ? `${referralLinks.length} market${referralLinks.length !== 1 ? "s" : ""} configured` : "Not configured"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Referral Links */}
        <Card className="mb-6" data-testid="card-referral-links">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-base">Referral Links</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Add your PadSplit referral links for each market you operate in. After a lead submits their info, they'll be directed to the matching referral link based on the property's location.
              </CardDescription>
            </div>
            <Button
              size="sm"
              onClick={() => setShowAddLink(true)}
              data-testid="button-add-referral-link"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add a Link
            </Button>
          </CardHeader>
          <CardContent>
            {settingsQuery.isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : referralLinks.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <ExternalLink className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium">No referral links configured</p>
                <p className="text-xs mt-1">Add your PadSplit referral link for each market to start routing leads.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {referralLinks.map((link, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg bg-white"
                    data-testid={`referral-link-row-${index}`}
                  >
                    {editingLinkIndex === index ? (
                      <div className="p-3 space-y-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block">Market Name</Label>
                            <Input
                              value={editLinkMarket}
                              onChange={(e) => setEditLinkMarket(e.target.value)}
                              placeholder="e.g. Dallas, Tampa"
                              className="h-8 text-sm"
                              data-testid={`input-edit-link-market-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block">PadSplit Referral URL</Label>
                            <Input
                              value={editLinkUrl}
                              onChange={(e) => setEditLinkUrl(e.target.value)}
                              placeholder="https://www.padsplit.com/..."
                              className="h-8 text-sm"
                              data-testid={`input-edit-link-url-${index}`}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            onClick={handleSaveEditLink}
                            disabled={saveMutation.isPending}
                            data-testid={`button-save-edit-link-${index}`}
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={cancelEditLink}
                            data-testid={`button-cancel-edit-link-${index}`}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-gray-900">{link.marketName}</p>
                            {link.isDefault && (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-200 bg-amber-50">
                                <Star className="h-2.5 w-2.5 mr-0.5 fill-amber-500" />
                                Default
                              </Badge>
                            )}
                          </div>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5 truncate"
                          >
                            {link.url} <ExternalLink className="h-3 w-3 flex-shrink-0" />
                          </a>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-gray-500 hover:text-blue-600"
                            onClick={() => startEditLink(index)}
                            data-testid={`button-edit-link-${index}`}
                            title="Edit link"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {!link.isDefault && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-gray-500 hover:text-amber-600"
                              onClick={() => handleSetDefault(index)}
                              data-testid={`button-set-default-${index}`}
                              title="Set as default fallback"
                            >
                              <Star className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-gray-500 hover:text-red-600"
                            onClick={() => handleRemoveLink(index)}
                            data-testid={`button-remove-link-${index}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <p className="text-xs text-gray-400 pt-1">
                  The default link is used when a property's location doesn't match any market name.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Landing Page URL (read-only primary + secondary pages) */}
        <Card className="mb-6" data-testid="card-landing-page-url">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Landing Page URL
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Your primary landing page URL is automatically generated from your account. Use additional landing pages to target specific property groups.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Primary URL (read-only) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-700">Primary URL</p>
                <Badge
                  variant="outline"
                  className="text-xs text-blue-700 border-blue-200 bg-blue-50 gap-1 font-normal"
                  data-testid="badge-primary-lead-count"
                >
                  <Users className="h-3 w-3" />
                  {primaryLeadCount} {primaryLeadCount === 1 ? "Lead" : "Leads"}
                </Badge>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg" data-tour="direct-bookings-settings">
                <span className="text-sm text-gray-800 flex-1 font-mono break-all" data-testid="text-primary-url">
                  {window.location.origin}{primaryUrl}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 flex-shrink-0 text-gray-500 hover:text-blue-600"
                  onClick={() => copyToClipboard(primaryUrl)}
                  data-testid="button-copy-primary-url"
                  title="Copy URL"
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 flex-shrink-0 text-gray-500 hover:text-blue-600"
                  onClick={() => window.open(primaryUrl, "_blank")}
                  data-testid="button-open-primary-url"
                  title="Open in new tab"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 flex-shrink-0 text-gray-500 hover:text-blue-600"
                  onClick={() => { closeAllSheets(); setShowPrimaryHouseSheet(true); }}
                  data-testid="button-primary-house"
                  title="Property visibility"
                >
                  <Home className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 flex-shrink-0 text-gray-500 hover:text-blue-600"
                  onClick={openPrimaryGearSheet}
                  data-testid="button-primary-gear"
                  title="Landing page settings"
                >
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Secondary landing pages */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-medium text-gray-700">Additional Landing Pages</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => { setShowAddPage(true); setNewPageSlug(""); setNewPageGroupId(""); setNewPageSlugError(""); }}
                  data-testid="button-add-landing-page"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Page
                </Button>
              </div>
              <p className="text-xs text-gray-400 mb-2 flex items-start gap-1">
                <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                Each secondary page inherits the global defaults below. Click the settings icon to override headline, branding, colors, and contact info for that page individually.
              </p>

              {landingPagesQuery.isLoading ? (
                <Skeleton className="h-12 w-full" />
              ) : secondaryPages.length === 0 && !showAddPage ? (
                <div className="text-center py-6 text-gray-400 border border-dashed border-gray-200 rounded-lg">
                  <Globe className="h-6 w-6 mx-auto mb-1.5 text-gray-300" />
                  <p className="text-xs">No additional landing pages yet.</p>
                  <p className="text-xs mt-0.5">Add one to target a specific property group.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {secondaryPages.map((page) => {
                    const groupName = propertyGroups.find(g => g.id === page.propertyGroupId)?.name;
                    const activeCount = page.activePropertyCount ?? 0;
                    const totalCount = page.totalPropertyCount ?? 0;
                    const hasNoActiveProperties = groupName != null && activeCount === 0;
                    const emptyReason: "no-properties" | "all-inactive" | null = hasNoActiveProperties
                      ? (totalCount === 0 ? "no-properties" : "all-inactive")
                      : null;
                    const emptyBadgeLabel = emptyReason === "no-properties"
                      ? "Group has no properties"
                      : "All properties inactive";
                    const emptyBadgeTooltip = emptyReason === "no-properties"
                      ? `The "${groupName}" group is empty. Add properties to this group so they appear on this landing page.`
                      : `The "${groupName}" group has ${totalCount} ${totalCount === 1 ? "property" : "properties"}, but none are set to Active. Change their status to Active or pick a different group so visitors see listings.`;
                    return (
                    <div
                      key={page.id}
                      className="border border-gray-200 rounded-lg bg-white"
                      data-testid={`secondary-page-row-${page.id}`}
                    >
                      <div className="flex items-center gap-3 p-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-mono text-gray-800 truncate">
                              /rooms-for-rent/<span className="font-semibold">{page.slug}</span>
                            </p>
                            {(() => {
                              const c = leadCountsQuery.data?.bySlug?.[page.slug] ?? 0;
                              return (
                                <Badge
                                  variant="outline"
                                  className="text-xs text-blue-700 border-blue-200 bg-blue-50 gap-1 font-normal"
                                  data-testid={`badge-leads-${page.id}`}
                                >
                                  <Users className="h-3 w-3" />
                                  {c} {c === 1 ? "Lead" : "Leads"}
                                </Badge>
                              );
                            })()}
                            {emptyReason && (
                              <Badge
                                variant="outline"
                                className="text-xs border-amber-300 bg-amber-50 text-amber-700 gap-1 font-normal"
                                title={emptyBadgeTooltip}
                                data-testid={`badge-empty-group-${page.id}`}
                              >
                                <AlertTriangle className="h-3 w-3" />
                                {emptyBadgeLabel}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {groupName
                              ? <>Group: <span className="font-medium text-gray-700">{groupName}</span></>
                              : <span className="italic text-amber-600">Group not found</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-gray-500 hover:text-blue-600"
                            onClick={() => window.open(`/rooms-for-rent/${page.slug}`, "_blank")}
                            data-testid={`button-open-page-${page.id}`}
                            title="Open"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-gray-500 hover:text-blue-600"
                            onClick={() => openPageHouseSheet(page)}
                            data-testid={`button-house-page-${page.id}`}
                            title="Property visibility"
                          >
                            <Home className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-gray-500 hover:text-blue-600"
                            onClick={() => openDrawer(page)}
                            data-testid={`button-edit-page-${page.id}`}
                            title="Edit settings"
                          >
                            <Settings className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-gray-500 hover:text-red-600"
                            onClick={() => deletePageMutation.mutate(page.id)}
                            disabled={deletePageMutation.isPending}
                            data-testid={`button-delete-page-${page.id}`}
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}

              {/* Add page form (inline) */}
              {showAddPage && (
                <div className="mt-2 border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-medium text-blue-800">New Landing Page</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Slug</Label>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-400 whitespace-nowrap">/rooms-for-rent/</span>
                        <Input
                          value={newPageSlug}
                          onChange={(e) => { setNewPageSlug(cleanSlug(e.target.value)); setNewPageSlugError(""); }}
                          placeholder="my-slug"
                          className="h-8 text-sm bg-white"
                          data-testid="input-new-page-slug"
                          autoFocus
                        />
                      </div>
                      {newPageSlugError && <p className="text-xs text-red-500 mt-1">{newPageSlugError}</p>}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Property Group <span className="text-red-500">*</span></Label>
                      <Select value={newPageGroupId} onValueChange={setNewPageGroupId}>
                        <SelectTrigger className="h-8 text-sm bg-white" data-testid="select-new-page-group">
                          <SelectValue placeholder="Select a group" />
                        </SelectTrigger>
                        <SelectContent>
                          {propertyGroups.map(g => (
                            <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="h-7 text-xs"
                      onClick={handleAddPage}
                      disabled={createPageMutation.isPending || !newPageSlug.trim()}
                      data-testid="button-save-new-page"
                    >
                      {createPageMutation.isPending ? "Saving..." : "Save"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => { setShowAddPage(false); setNewPageSlugError(""); }}
                      data-testid="button-cancel-new-page"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>


        <Card data-testid="card-lead-inbox">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">Lead Inbox</CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  Prospective members who submitted their info through the landing page.
                </CardDescription>
              </div>
              {leads.length > 0 && (
                <Select value={leadSourceFilter} onValueChange={setLeadSourceFilter}>
                  <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="select-lead-source-filter">
                    <SelectValue placeholder="Filter by source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="option-source-all">All sources</SelectItem>
                    <SelectItem value="primary" data-testid="option-source-primary">Primary</SelectItem>
                    {secondaryPages.map((page) => (
                      <SelectItem key={page.id} value={`slug:${page.slug}`} data-testid={`option-source-${page.slug}`}>
                        /{page.slug}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {leadsQuery.isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : leads.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium">No inquiries yet</p>
                <p className="text-xs mt-1">Share your landing page link to start capturing leads.</p>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-10 text-gray-400" data-testid="empty-leads-filter">
                <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium">No leads from this source</p>
                <p className="text-xs mt-1">Try a different source filter to see more leads.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredLeads.map((lead) => (
                  <div key={lead.id} className="py-3 flex items-start gap-4" data-testid={`lead-row-${lead.id}`}>
                    <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-blue-600">{lead.name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900" data-testid={`lead-name-${lead.id}`}>{lead.name}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {lead.email}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {lead.phone}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge
                        variant="outline"
                        className={lead.landingPageSlug
                          ? "text-xs text-purple-600 border-purple-200 bg-purple-50"
                          : "text-xs text-gray-600 border-gray-200 bg-gray-50"}
                        data-testid={`badge-source-${lead.id}`}
                      >
                        {lead.landingPageSlug ? `/${lead.landingPageSlug}` : "Primary"}
                      </Badge>
                      <Badge variant="outline" className="text-xs text-blue-600 border-blue-200 bg-blue-50">
                        direct inquiry
                      </Badge>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(lead.createdAt), "MMM d, yyyy")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showAddLink} onOpenChange={(open) => { if (!open) { setShowAddLink(false); setNewLinkMarket(""); setNewLinkUrl(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-add-referral-link">
          <DialogHeader>
            <DialogTitle>Add Referral Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label htmlFor="market-name" className="text-sm">Market Name</Label>
              <Input
                id="market-name"
                value={newLinkMarket}
                onChange={(e) => setNewLinkMarket(e.target.value)}
                placeholder="e.g. Dallas, Tampa, Atlanta"
                className="mt-1"
                data-testid="input-market-name"
              />
              <p className="text-xs text-gray-400 mt-1">This name will be matched against property addresses to route leads.</p>
            </div>
            <div>
              <Label htmlFor="referral-url" className="text-sm">PadSplit Referral Link</Label>
              <Input
                id="referral-url"
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                placeholder="https://www.padsplit.com/..."
                className="mt-1"
                data-testid="input-referral-url"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                onClick={handleAddLink}
                disabled={saveMutation.isPending || !newLinkMarket.trim() || !newLinkUrl.trim()}
                data-testid="button-save-referral-link"
              >
                {saveMutation.isPending ? "Saving..." : "Save Link"}
              </Button>
              <Button variant="outline" onClick={() => { setShowAddLink(false); setNewLinkMarket(""); setNewLinkUrl(""); }} data-testid="button-cancel-add-link">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Per-page settings drawer */}
      {/* In-flight dismissal gating (docs/dialog-dismissal-convention.md): this
          panel holds a save + delete + logo upload, so block dismissal mid-write. */}
      <Sheet open={!!drawerPage} onOpenChange={(open) => { if (!open && (updatePageMutation.isPending || deletePageMutation.isPending || drawerUploadingLogo)) return; if (!open) closeDrawer(); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto"
          data-testid="drawer-page-settings"
          onEscapeKeyDown={(e) => { if (updatePageMutation.isPending || deletePageMutation.isPending || drawerUploadingLogo) e.preventDefault(); }}
          onInteractOutside={(e) => { if (updatePageMutation.isPending || deletePageMutation.isPending || drawerUploadingLogo) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (updatePageMutation.isPending || deletePageMutation.isPending || drawerUploadingLogo) e.preventDefault(); }}
        >
          <SheetHeader className="mb-4">
            <SheetTitle>Page Settings</SheetTitle>
            <SheetDescription>
              {drawerPage ? (
                <>
                  Customize <span className="font-mono font-medium">/rooms-for-rent/{drawerPage.slug}</span>.
                  Leave any field blank to inherit the global default.
                </>
              ) : null}
            </SheetDescription>
          </SheetHeader>
          {drawerFormData && (
            <div className="space-y-5">
              {/* Slug & Group */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="text-sm font-medium">Slug</Label>
                  <div className="flex items-center gap-1 mt-1 px-3 py-2 rounded-md bg-gray-50 border border-gray-200" data-testid="text-drawer-slug">
                    <span className="text-xs text-gray-400 whitespace-nowrap">/rooms-for-rent/</span>
                    <span className="text-sm font-mono text-gray-700">{drawerFormData.slug}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">The slug can't be changed after a page is created — changing it would break any existing links or QR codes pointing to this page.</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Property Group <span className="text-red-500">*</span></Label>
                  <Select value={drawerFormData.propertyGroupId} onValueChange={(v) => setDrawerFormData({ ...drawerFormData, propertyGroupId: v })}>
                    <SelectTrigger className="mt-1" data-testid="select-drawer-group">
                      <SelectValue placeholder="Select a group" />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyGroups.map(g => (
                        <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Branding Overrides</p>
                <div className="grid grid-cols-1 gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm flex items-center gap-1.5">
                        <Type className="h-3.5 w-3.5" />
                        Brand Name
                      </Label>
                      <Input
                        value={drawerFormData.brandName}
                        onChange={(e) => setDrawerFormData({ ...drawerFormData, brandName: e.target.value })}
                        placeholder={settings?.brandName || "PadPulse (default)"}
                        className="mt-1"
                        data-testid="input-drawer-brand-name"
                      />
                    </div>
                    <div>
                      <Label className="text-sm flex items-center gap-1.5">
                        <Palette className="h-3.5 w-3.5" />
                        Accent Color
                      </Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={drawerFormData.accentColor || settings?.accentColor || DEFAULT_ACCENT_COLOR}
                          onChange={(e) => setDrawerFormData({ ...drawerFormData, accentColor: e.target.value })}
                          className="h-9 w-9 rounded border border-gray-200 cursor-pointer"
                          data-testid="input-drawer-accent-color"
                        />
                        <Input
                          value={drawerFormData.accentColor}
                          onChange={(e) => setDrawerFormData({ ...drawerFormData, accentColor: e.target.value })}
                          placeholder={settings?.accentColor || `${DEFAULT_ACCENT_COLOR} (default)`}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm flex items-center gap-1.5">
                      <Image className="h-3.5 w-3.5" />
                      Brand Logo
                    </Label>
                    <div className="mt-1 flex items-center gap-3">
                      {drawerFormData.brandLogoUrl ? (
                        <div className="relative">
                          <img src={drawerFormData.brandLogoUrl} alt="Brand logo" className="h-10 w-auto max-w-[120px] object-contain border rounded p-1" data-testid="img-drawer-logo-preview" />
                          <button
                            type="button"
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs hover:bg-red-600"
                            onClick={() => setDrawerFormData({ ...drawerFormData, brandLogoUrl: "" })}
                            data-testid="button-drawer-remove-logo"
                          >
                            ×
                          </button>
                        </div>
                      ) : null}
                      <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-md hover:bg-gray-50 transition-colors">
                        <Upload className="h-3.5 w-3.5" />
                        {drawerUploadingLogo ? "Uploading..." : drawerFormData.brandLogoUrl ? "Change" : "Upload Logo"}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          className="hidden"
                          onChange={handleDrawerLogoUpload}
                          disabled={drawerUploadingLogo}
                          data-testid="input-drawer-logo-upload"
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Leave blank to use the default logo.</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Copy Overrides</p>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm flex items-center gap-1.5">
                        <Type className="h-3.5 w-3.5" />
                        Page Headline
                      </Label>
                      <Input
                        value={drawerFormData.headline}
                        onChange={(e) => setDrawerFormData({ ...drawerFormData, headline: e.target.value })}
                        placeholder={settings?.headline || "Rooms for Rent (default)"}
                        className="mt-1"
                        data-testid="input-drawer-headline"
                      />
                    </div>
                    <div>
                      <Label className="text-sm flex items-center gap-1.5">
                        <MessageSquare className="h-3.5 w-3.5" />
                        Tagline
                      </Label>
                      <Input
                        value={drawerFormData.tagline}
                        onChange={(e) => setDrawerFormData({ ...drawerFormData, tagline: e.target.value })}
                        placeholder={settings?.tagline || "Start by choosing a home… (default)"}
                        className="mt-1"
                        data-testid="input-drawer-tagline"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5" />
                      Page Description
                    </Label>
                    <Textarea
                      value={drawerFormData.description}
                      onChange={(e) => setDrawerFormData({ ...drawerFormData, description: e.target.value })}
                      placeholder={settings?.description || "Find affordable furnished rooms… (default)"}
                      className="mt-1"
                      rows={3}
                      data-testid="input-drawer-description"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">CTA Button Text</Label>
                      <Input
                        value={drawerFormData.ctaButtonText}
                        onChange={(e) => setDrawerFormData({ ...drawerFormData, ctaButtonText: e.target.value })}
                        placeholder={settings?.ctaButtonText || "Start Free Application (default)"}
                        className="mt-1"
                        data-testid="input-drawer-cta"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Footer Text</Label>
                      <Input
                        value={drawerFormData.footerText}
                        onChange={(e) => setDrawerFormData({ ...drawerFormData, footerText: e.target.value })}
                        placeholder={settings?.footerText || "Footer text (default)"}
                        className="mt-1"
                        data-testid="input-drawer-footer-text"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Contact Overrides</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm flex items-center gap-1.5">
                      <Phone className="h-3.5 w-3.5" />
                      Contact Phone
                    </Label>
                    <Input
                      type="tel"
                      value={drawerFormData.contactPhone}
                      onChange={(e) => setDrawerFormData({ ...drawerFormData, contactPhone: e.target.value })}
                      placeholder={settings?.contactPhone || "(555) 000-0000 (default)"}
                      className="mt-1"
                      data-testid="input-drawer-contact-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-sm flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5" />
                      Contact Email
                    </Label>
                    <Input
                      type="email"
                      value={drawerFormData.contactEmail}
                      onChange={(e) => setDrawerFormData({ ...drawerFormData, contactEmail: e.target.value })}
                      placeholder={settings?.contactEmail || "contact@example.com (default)"}
                      className="mt-1"
                      data-testid="input-drawer-contact-email"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-2 pb-4">
                <Button
                  onClick={handleSaveDrawer}
                  disabled={updatePageMutation.isPending}
                  data-testid="button-drawer-save"
                >
                  {updatePageMutation.isPending ? "Saving..." : "Save"}
                </Button>
                <Button variant="outline" onClick={closeDrawer} data-testid="button-drawer-cancel">
                  Cancel
                </Button>
                <Button
                  variant="ghost"
                  className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => {
                    if (drawerPage) {
                      deletePageMutation.mutate(drawerPage.id);
                      closeDrawer();
                    }
                  }}
                  disabled={deletePageMutation.isPending}
                  data-testid="button-drawer-delete"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Delete Page
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Primary URL gear sheet — global landing page settings */}
      {/* In-flight dismissal gating (docs/dialog-dismissal-convention.md): holds a
          settings save + logo upload, so block dismissal mid-write. */}
      <Sheet open={showPrimaryGearSheet} onOpenChange={(open) => { if (!open && (saveMutation.isPending || uploadingLogo)) return; if (!open) closePrimaryGearSheet(); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto"
          data-testid="sheet-primary-gear"
          onEscapeKeyDown={(e) => { if (saveMutation.isPending || uploadingLogo) e.preventDefault(); }}
          onInteractOutside={(e) => { if (saveMutation.isPending || uploadingLogo) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (saveMutation.isPending || uploadingLogo) e.preventDefault(); }}
        >
          <SheetHeader className="mb-4">
            <SheetTitle>Landing Page Settings</SheetTitle>
            <SheetDescription>
              These settings apply to your primary landing page and act as defaults for any secondary page that hasn't overridden them.
            </SheetDescription>
          </SheetHeader>
          {formData && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gear-brandName" className="text-sm flex items-center gap-1.5">
                    <Type className="h-3.5 w-3.5" />
                    Brand Name
                  </Label>
                  <Input
                    id="gear-brandName"
                    value={formData.brandName}
                    onChange={(e) => setFormData({ ...formData, brandName: e.target.value })}
                    placeholder="PadPulse"
                    className="mt-1"
                    data-testid="input-brand-name"
                  />
                  <p className="text-xs text-gray-400 mt-1">Replaces the default logo text on the landing page.</p>
                </div>
                <div>
                  <Label className="text-sm flex items-center gap-1.5">
                    <Image className="h-3.5 w-3.5" />
                    Brand Logo
                  </Label>
                  <div className="mt-1 flex items-center gap-3">
                    {formData.brandLogoUrl ? (
                      <div className="relative">
                        <img src={formData.brandLogoUrl} alt="Brand logo" className="h-10 w-auto max-w-[120px] object-contain border rounded p-1" data-testid="img-brand-logo-preview" />
                        <button
                          type="button"
                          className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs hover:bg-red-600"
                          onClick={() => setFormData({ ...formData, brandLogoUrl: "" })}
                          data-testid="button-remove-logo"
                        >
                          ×
                        </button>
                      </div>
                    ) : null}
                    <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-md hover:bg-gray-50 transition-colors">
                      <Upload className="h-3.5 w-3.5" />
                      {uploadingLogo ? "Uploading..." : formData.brandLogoUrl ? "Change" : "Upload Logo"}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="hidden"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                        data-testid="input-logo-upload"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Shown in header and footer on the landing page.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gear-headline" className="text-sm flex items-center gap-1.5">
                    <Type className="h-3.5 w-3.5" />
                    Page Headline
                  </Label>
                  <Input
                    id="gear-headline"
                    value={formData.headline}
                    onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                    placeholder="Rooms for Rent"
                    className="mt-1"
                    data-testid="input-headline"
                  />
                </div>
                <div>
                  <Label htmlFor="gear-tagline" className="text-sm flex items-center gap-1.5">
                    <MessageSquare className="h-3.5 w-3.5" />
                    Tagline
                  </Label>
                  <Input
                    id="gear-tagline"
                    value={formData.tagline}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                    placeholder="Start by choosing a home — then pick an available room inside."
                    className="mt-1"
                    data-testid="input-tagline"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="gear-description" className="text-sm flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />
                  Page Description
                </Label>
                <Textarea
                  id="gear-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Find affordable furnished rooms available now..."
                  className="mt-1"
                  rows={3}
                  data-testid="input-description"
                />
                <p className="text-xs text-gray-400 mt-1">Shows below the headline on the landing page.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gear-ctaButtonText" className="text-sm">CTA Button Text</Label>
                  <Input
                    id="gear-ctaButtonText"
                    value={formData.ctaButtonText}
                    onChange={(e) => setFormData({ ...formData, ctaButtonText: e.target.value })}
                    placeholder="Start Free Application"
                    className="mt-1"
                    data-testid="input-cta-button-text"
                  />
                </div>
                <div>
                  <Label htmlFor="gear-accentColor" className="text-sm flex items-center gap-1.5">
                    <Palette className="h-3.5 w-3.5" />
                    Accent Color
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      id="gear-accentColor"
                      value={formData.accentColor}
                      onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                      className="h-9 w-9 rounded border border-gray-200 cursor-pointer"
                      data-testid="input-accent-color"
                    />
                    <Input
                      value={formData.accentColor}
                      onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                      className="flex-1"
                      placeholder={DEFAULT_ACCENT_COLOR}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gear-contactPhone" className="text-sm flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5" />
                    Contact Phone
                  </Label>
                  <Input
                    id="gear-contactPhone"
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    placeholder="(555) 000-0000"
                    className="mt-1"
                    data-testid="input-contact-phone"
                  />
                  <p className="text-xs text-gray-400 mt-1">Shown in a contact section on the landing page.</p>
                </div>
                <div>
                  <Label htmlFor="gear-contactEmail" className="text-sm flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5" />
                    Contact Email
                  </Label>
                  <Input
                    id="gear-contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    placeholder="contact@example.com"
                    className="mt-1"
                    data-testid="input-contact-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="gear-footerText" className="text-sm">Footer Text</Label>
                <Textarea
                  id="gear-footerText"
                  value={formData.footerText}
                  onChange={(e) => setFormData({ ...formData, footerText: e.target.value })}
                  placeholder="PadPulse helps PadSplit hosts manage and grow their rental portfolio."
                  className="mt-1"
                  rows={2}
                  data-testid="input-footer-text"
                />
              </div>
              <div className="flex gap-2 pt-1 pb-4">
                <Button
                  onClick={handleSavePrimaryGear}
                  disabled={saveMutation.isPending}
                  data-testid="button-save-settings"
                >
                  {saveMutation.isPending ? "Saving..." : "Save Settings"}
                </Button>
                <Button variant="outline" onClick={closePrimaryGearSheet} data-testid="button-cancel-settings">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Primary URL house sheet — property visibility */}
      {/* In-flight dismissal gating (docs/dialog-dismissal-convention.md): holds a
          visibility save, so block dismissal mid-write. */}
      <Sheet open={showPrimaryHouseSheet} onOpenChange={(open) => { if (!open && saveMutation.isPending) return; if (!open) setShowPrimaryHouseSheet(false); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md overflow-y-auto"
          data-testid="sheet-primary-house"
          onEscapeKeyDown={(e) => { if (saveMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (saveMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (saveMutation.isPending) e.preventDefault(); }}
        >
          <SheetHeader className="mb-4">
            <SheetTitle>Property Visibility</SheetTitle>
            <SheetDescription>
              Choose which properties appear on your primary landing page. If none are selected, all active properties will be shown.
            </SheetDescription>
          </SheetHeader>
          {propertiesQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : allProperties.length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              <Home className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No properties found.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {allProperties.map((property) => {
                const isEnabled = enabledPropertyIds.length === 0 || enabledPropertyIds.includes(property.id);
                return (
                  <div
                    key={property.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                    data-testid={`property-visibility-${property.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Home className="h-4 w-4 text-gray-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{property.name}</p>
                        {property.address && (
                          <p className="text-xs text-gray-500">{property.address}</p>
                        )}
                      </div>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={(checked) => {
                        if (enabledPropertyIds.length === 0) {
                          const allIds = allProperties.map(p => p.id).filter(id => id !== property.id);
                          saveMutation.mutate({ enabledPropertyIds: checked ? allProperties.map(p => p.id) : allIds });
                        } else {
                          toggleProperty(property.id, checked);
                        }
                      }}
                      data-testid={`switch-property-${property.id}`}
                    />
                  </div>
                );
              })}
              <p className="text-xs text-gray-400 pt-1">
                {enabledPropertyIds.length > 0
                  ? `${enabledPropertyIds.length} of ${allProperties.length} properties visible on landing page.`
                  : "All properties are currently visible (no filter applied)."}
              </p>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Additional landing page house sheet — per-page property visibility */}
      {(() => {
        const page = pageHouseSheetPage;
        const group = page ? propertyGroups.find(g => g.id === page.propertyGroupId) : null;
        const groupProperties = group
          ? allProperties.filter(p => p.propertyGroup === group.name)
          : [];
        // In-flight dismissal gating (docs/dialog-dismissal-convention.md):
        // holds a visibility save, so block dismissal mid-write.
        return (
          <Sheet open={!!pageHouseSheetPage} onOpenChange={(open) => { if (!open && updatePageVisibilityMutation.isPending) return; if (!open) setPageHouseSheetPage(null); }}>
            <SheetContent
              side="right"
              className="w-full sm:max-w-md overflow-y-auto"
              data-testid="sheet-page-house"
              onEscapeKeyDown={(e) => { if (updatePageVisibilityMutation.isPending) e.preventDefault(); }}
              onInteractOutside={(e) => { if (updatePageVisibilityMutation.isPending) e.preventDefault(); }}
              onPointerDownOutside={(e) => { if (updatePageVisibilityMutation.isPending) e.preventDefault(); }}
            >
              <SheetHeader className="mb-4">
                <SheetTitle>Property Visibility</SheetTitle>
                <SheetDescription>
                  {page ? (
                    <>
                      Toggle which properties from the <span className="font-medium">{group?.name ?? "selected"}</span> group appear on{" "}
                      <span className="font-mono font-medium">/rooms-for-rent/{page.slug}</span>.
                      By default all properties in the group are shown.
                    </>
                  ) : null}
                </SheetDescription>
              </SheetHeader>
              {page && (
                <div className="space-y-3">
                  {groupProperties.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 border border-dashed border-gray-200 rounded-lg">
                      <Home className="h-6 w-6 mx-auto mb-1.5 text-gray-300" />
                      <p className="text-sm">No properties in this group.</p>
                      <p className="text-xs mt-0.5">Add properties to this group to manage their visibility here.</p>
                    </div>
                  ) : (
                    <>
                      {groupProperties.map((property) => {
                        const isEnabled = pageHouseSheetEnabledIds.length === 0 || pageHouseSheetEnabledIds.includes(property.id);
                        return (
                          <div
                            key={property.id}
                            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                            data-testid={`page-property-visibility-${property.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center">
                                <Home className="h-4 w-4 text-gray-500" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{property.name}</p>
                                {property.address && (
                                  <p className="text-xs text-gray-500">{property.address}</p>
                                )}
                              </div>
                            </div>
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={(checked) => {
                                if (pageHouseSheetEnabledIds.length === 0) {
                                  const allIds = groupProperties.map(p => p.id).filter(id => id !== property.id);
                                  setPageHouseSheetEnabledIds(checked ? [] : allIds);
                                } else if (checked) {
                                  const updated = [...pageHouseSheetEnabledIds, property.id];
                                  setPageHouseSheetEnabledIds(updated.length === groupProperties.length ? [] : updated);
                                } else {
                                  setPageHouseSheetEnabledIds(pageHouseSheetEnabledIds.filter(id => id !== property.id));
                                }
                              }}
                              data-testid={`switch-page-property-${property.id}`}
                            />
                          </div>
                        );
                      })}
                      <p className="text-xs text-gray-400 pt-1">
                        {pageHouseSheetEnabledIds.length > 0
                          ? `${pageHouseSheetEnabledIds.length} of ${groupProperties.length} properties visible on this page.`
                          : "All properties in this group are visible (no filter applied)."}
                      </p>
                    </>
                  )}
                  <div className="flex gap-2 pt-2 pb-4">
                    <Button
                      onClick={() => {
                        if (!page) return;
                        const ids = pageHouseSheetEnabledIds.length === 0 ? null : pageHouseSheetEnabledIds;
                        updatePageVisibilityMutation.mutate({ id: page.id, enabledPropertyIds: ids });
                      }}
                      disabled={updatePageVisibilityMutation.isPending}
                      data-testid="button-save-page-visibility"
                    >
                      {updatePageVisibilityMutation.isPending ? "Saving..." : "Save"}
                    </Button>
                    <Button variant="outline" onClick={() => setPageHouseSheetPage(null)} data-testid="button-cancel-page-visibility">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </SheetContent>
          </Sheet>
        );
      })()}
    </AppLayout>
  );
}
